import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import Axios from 'axios';
import React, {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { match, P } from 'ts-pattern';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { usePodApiAccessTokenContext } from './PodApiAccessTokenProvider';
import { useWorkspace } from './WorkspaceProvider';

// Create a client with default options
const getQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        refetchOnReconnect: true,
        // Keep data fresh for 5 minutes
        staleTime: 5 * 60 * 1000,
      },
    },
  });

type PaymentsApiHttpClientContextValue = {
  client: AxiosInstance;
  hasWorkspace: boolean;
};

const PaymentsApiHttpClient = createContext<PaymentsApiHttpClientContextValue>({
  client: Axios.create(),
  hasWorkspace: false,
});

/**
 * Provider that sets the authentication token for the Payments API.
 * This should be rendered within the PodApiAccessTokenProvider context.
 */
export const PaymentsApiAuthProvider = ({ children }: PropsWithChildren) => {
  const { workspace } = useWorkspace();
  const { customerApiToken } = usePodApiAccessTokenContext();
  const [queryClient, setQueryClient] = useState(getQueryClient());

  const axiosInstance = useMemo(() => {
    const baseURL = match(workspace?.pod.customerApiUrl)
      .with(P.string, (url) => url.replace('graphql', 'dashboard'))
      .otherwise(() => '');

    return Axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
        'X-Fragment-Workspace': workspace?.id,
        'X-Fragment-Client': 'dashboard',
        Authorization: `Bearer ${customerApiToken}`,
      },
    });
  }, [workspace, customerApiToken]);

  useEffect(() => {
    // Instantiate a new client on workspace change to reset cached queries.
    setQueryClient(getQueryClient());
  }, [workspace?.id]);

  const contextValue = useMemo(
    () => ({
      client: axiosInstance,
      hasWorkspace: !!workspace,
    }),
    [axiosInstance, workspace]
  );

  return (
    <PaymentsApiHttpClient.Provider value={contextValue}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </PaymentsApiHttpClient.Provider>
  );
};

export function usePaymentsApiHttpClient<T>() {
  const { client } = useContext(PaymentsApiHttpClient);
  return async (options?: AxiosRequestConfig & { body?: string }): Promise<T> =>
    client({ ...options }).then(({ data }) => data);
}

export function usePaymentsApiHttpClientContext() {
  const contextValue = useContext(PaymentsApiHttpClient);
  return contextValue;
}
